import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ImageIcon, Link2, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Solid Extrusions", "Sponge Extrusions", "Reinforced Hoses", "Molded Products"];
const EMPTY = { name: "", category: "Solid Extrusions", description: "", specsText: "", featuresText: "", applicationsText: "", imagesText: "", source_url: "" };

const toForm = (p) => ({
  name: p.name, category: p.category, description: p.description || "",
  specsText: (p.specs || []).map((s) => `${s.label}: ${s.value}`).join("\n"),
  featuresText: (p.features || []).join("\n"),
  applicationsText: (p.applications || []).join("\n"),
  imagesText: (p.images || []).join("\n"),
  source_url: p.source_url || "",
});

const toPayload = (f) => ({
  name: f.name, category: f.category, description: f.description, source_url: f.source_url,
  specs: f.specsText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const i = l.indexOf(":");
    return i > 0 ? { label: l.slice(0, i).trim(), value: l.slice(i + 1).trim() } : { label: l, value: "" };
  }),
  features: f.featuresText.split("\n").map((l) => l.trim()).filter(Boolean),
  applications: f.applicationsText.split("\n").map((l) => l.trim()).filter(Boolean),
  images: f.imagesText.split("\n").map((l) => l.trim()).filter(Boolean),
});

export default function CatalogueAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, form}
  const [importUrl, setImportUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const load = () => api.get("/catalogue").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const fetchFromLink = async () => {
    setFetching(true);
    try {
      const r = await api.post("/catalogue/scrape", { url: importUrl });
      const d = r.data;
      setEditing((e) => ({ ...e, form: {
        name: d.name, category: d.category, description: d.description,
        specsText: (d.specs || []).map((s) => `${s.label}: ${s.value}`).join("\n"),
        featuresText: (d.features || []).join("\n"),
        applicationsText: (d.applications || []).join("\n"),
        imagesText: (d.images || []).join("\n"),
        source_url: d.source_url,
      }}));
      toast.success(`Fetched "${d.name}" — ${d.images.length} image${d.images.length === 1 ? "" : "s"} found. Review & save.`);
    } catch (e) { toast.error(formatApiError(e)); }
    setFetching(false);
  };

  const save = async () => {
    try {
      const payload = toPayload(editing.form);
      if (editing.id) await api.patch(`/catalogue/${editing.id}`, payload);
      else await api.post("/catalogue", payload);
      toast.success(`Product ${editing.id ? "updated" : "added"}`);
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the catalogue?`)) return;
    try { await api.delete(`/catalogue/${p.id}`); toast.success("Product removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const set = (k, v) => setEditing((e) => ({ ...e, form: { ...e.form, [k]: v } }));

  const [syncing, setSyncing] = useState(false);
  const syncAll = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/catalogue/sync-all", {}, { timeout: 120000 });
      toast.success(`${r.data.updated} products refreshed from your website${r.data.failed.length ? ` — failed: ${r.data.failed.join(", ")}` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSyncing(false);
  };

  return (
    <div className="space-y-6" data-testid="catalogue-admin-page">
      <PageHeader title="Catalogue" subtitle="Client portal product showcase"
                  right={<div className="flex items-center gap-2">
                    <Button variant="outline" onClick={syncAll} disabled={syncing} className="border-slate-200" data-testid="cat-sync-btn">
                      <RefreshCw size={14} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync All from Website"}
                    </Button>
                    <Button onClick={() => { setEditing({ id: null, form: { ...EMPTY } }); setImportUrl(""); }} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="cat-add-btn"><Plus size={14} className="mr-1.5" /> Add Product</Button>
                  </div>} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((p) => (
          <Card key={p.id} className="border-slate-200 rounded-xl shadow-none overflow-hidden group" data-testid={`cat-item-${p.id}`}>
            <div className="h-36 bg-slate-50 flex items-center justify-center overflow-hidden">
              {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="h-full w-full object-contain p-2" /> :
                <ImageIcon size={22} className="text-slate-300" />}
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-[#FC5F5F] font-semibold">{p.category}</div>
              <div className="text-sm font-semibold text-slate-800 mt-0.5 leading-snug">{p.name}</div>
              <div className="flex items-center gap-1.5 mt-3">
                <button onClick={() => { setEditing({ id: p.id, form: toForm(p) }); setImportUrl(p.source_url || ""); }} className="p-1.5 text-slate-400 hover:text-[#3B82F6]" data-testid={`cat-edit-${p.id}`}><Pencil size={13} /></button>
                <button onClick={() => del(p)} className="p-1.5 text-slate-400 hover:text-rose-600" data-testid={`cat-del-${p.id}`}><Trash2 size={13} /></button>
                <span className="ml-auto text-[10px] text-slate-400">{p.images?.length || 0} image{(p.images?.length || 0) === 1 ? "" : "s"}</span>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-sm text-slate-400 text-center py-12">No catalogue products yet — add your first one.</div>}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto dialog-slide-right">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Product" : "Add Product"}</DialogTitle>
            <DialogDescription className="text-xs">Paste a product link to auto-fill everything, or fill the fields manually.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="rounded-xl border border-[#3B82F6]/15 bg-gradient-to-br from-slate-50 to-[#EDE8F9]/40 p-4" data-testid="cat-import-box">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-[#3B82F6]">
                  <Sparkles size={13} className="text-[#FC5F5F]" /> Import from product link
                </div>
                <div className="flex gap-2 mt-2">
                  <Input placeholder="https://hitechsilico.com/your-product/" value={importUrl}
                         onChange={(e) => setImportUrl(e.target.value)}
                         onKeyDown={(e) => e.key === "Enter" && importUrl && !fetching && fetchFromLink()}
                         className="bg-white" data-testid="cat-import-url" />
                  <Button onClick={fetchFromLink} disabled={fetching || !importUrl.trim()}
                          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white whitespace-nowrap" data-testid="cat-import-fetch">
                    <Link2 size={13} className="mr-1.5" /> {fetching ? "Fetching…" : "Auto-Fill"}
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">Name, description, specs, features and all product images are picked up automatically — review below and hit Save.</p>
              </div>

              {editing.form.imagesText && (
                <div className="flex gap-2 flex-wrap" data-testid="cat-image-preview">
                  {editing.form.imagesText.split("\n").map((l) => l.trim()).filter(Boolean).slice(0, 6).map((u) => (
                    <div key={u} className="w-16 h-16 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
                      <img src={u} alt="" className="w-full h-full object-contain p-1" />
                    </div>
                  ))}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Name *</Label>
                  <Input value={editing.form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="cat-form-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Category</Label>
                  <Select value={editing.form.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="cat-form-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Description</Label>
                <Textarea rows={3} value={editing.form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" data-testid="cat-form-desc" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Specs (one per line, "Label: Value")</Label>
                  <Textarea rows={4} value={editing.form.specsText} onChange={(e) => set("specsText", e.target.value)} className="mt-1.5 font-mono text-xs" data-testid="cat-form-specs" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Key Features (one per line)</Label>
                  <Textarea rows={4} value={editing.form.featuresText} onChange={(e) => set("featuresText", e.target.value)} className="mt-1.5 text-xs" data-testid="cat-form-features" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Applications (one per line)</Label>
                  <Textarea rows={4} value={editing.form.applicationsText} onChange={(e) => set("applicationsText", e.target.value)} className="mt-1.5 text-xs" data-testid="cat-form-apps" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Image URLs (one per line)</Label>
                  <Textarea rows={4} value={editing.form.imagesText} onChange={(e) => set("imagesText", e.target.value)} className="mt-1.5 font-mono text-xs" data-testid="cat-form-images" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Website Link (optional)</Label>
                <Input value={editing.form.source_url} onChange={(e) => set("source_url", e.target.value)} className="mt-1.5" data-testid="cat-form-url" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={!editing?.form.name} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="cat-form-save">Save Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
